import { X } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { useDialogFocusTrap } from '../../hooks/useDialogFocusTrap';
import { useI18n } from '../../i18n/useI18n';
import type { Transaction } from '../../lib/api-types';
import { useUpsertOverride } from '../transactions/transactions.api';
import { formatLongDate } from './dashboard-format';

export function OccurrenceEditorDialog({
  occurrenceDate,
  onClose,
  transaction,
}: {
  readonly occurrenceDate: string;
  readonly onClose: () => void;
  readonly transaction: Transaction;
}) {
  const { locale, t } = useI18n();
  const override = transaction.overrides.find((item) => item.date === occurrenceDate);
  const [amount, setAmount] = useState(override?.amount ?? transaction.amount);
  const [additionalNote, setAdditionalNote] = useState(override?.additionalNote ?? '');
  const upsertOverride = useUpsertOverride();
  const dialogRef = useDialogFocusTrap(onClose);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await upsertOverride.mutateAsync({
      date: occurrenceDate,
      id: transaction.id,
      input: {
        additionalNote,
        amount,
        disabled: false,
        ...(override?.walletId === undefined ? {} : { walletId: override.walletId }),
      },
    });
    onClose();
  };

  return (
    <div className="dialog-backdrop" role="presentation">
      <section
        aria-labelledby="edit-occurrence-title"
        aria-modal="true"
        className="movement-dialog occurrence-editor-sheet"
        ref={dialogRef}
        role="dialog"
      >
        <div className="dialog-heading">
          <div>
            <p className="section-kicker">{t('plan.occurrence')}</p>
            <h2 id="edit-occurrence-title">{formatLongDate(occurrenceDate, locale)}</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <p className="occurrence-editor-description">{t('plan.occurrenceDescription')}</p>
        <form className="movement-form" onSubmit={submit}>
          <label className="field-wide">
            {t('dashboard.amount')}
            <input
              autoFocus
              inputMode="decimal"
              max="1000000000"
              min="0.01"
              required
              step="0.01"
              type="number"
              value={amount}
              onChange={(event) => setAmount(Number(event.target.value))}
            />
          </label>
          <label className="field-wide">
            {t('plan.occurrenceNote')} <span>{t('dashboard.optional')}</span>
            <textarea
              maxLength={120}
              rows={3}
              value={additionalNote}
              onChange={(event) => setAdditionalNote(event.target.value)}
            />
          </label>
          {upsertOverride.error !== null && (
            <p className="form-error field-wide" role="alert">
              {t('dashboard.saveError')}
            </p>
          )}
          <div className="dialog-actions field-wide">
            <button className="button button-ghost" type="button" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              className="button button-primary"
              disabled={amount <= 0 || upsertOverride.isPending}
              type="submit"
            >
              {upsertOverride.isPending ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
